/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/bolmateButton.scss';

import React, {CSSProperties, MouseEventHandler, PureComponent, ReactNode} from 'react';

import {ElementPosition, OrnamentShape, voidFunction} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import {BolmateControlAlign, BolmateControlColor, BolmateControlShape, BolmateControlSize} from '../../../util/types';

interface Props {
    onClick: MouseEventHandler;
    label: ReactNode;
    ornaments?: OrnamentShape | OrnamentShape[];
    disabled?: boolean;
    className?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    secondary?: boolean;
    tertiary?: boolean;
    color?: BolmateControlColor;
    size?: BolmateControlSize;
    shape?: BolmateControlShape;
    align?: BolmateControlAlign;
    id?: string;
}

export default class BolmateButton extends PureComponent<Props> {
    getContainerStyle = (): CSSProperties => {
        const style: CSSProperties = {gridTemplateColumns: '1fr'},
            {ornaments} = this.props;
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) style.gridTemplateColumns = '1fr';
        let hasLeft: boolean = false,
            hasRight: boolean = false;
        if (Array.isArray(ornaments))
            for (const ornament of ornaments) {
                if (!ornament.placement || ornament.placement === ElementPosition.LEFT) hasLeft = true;
                else if (ornament.placement === ElementPosition.RIGHT) hasRight = true;
                if (hasLeft && hasRight) break;
            }
        else if (ornaments) {
            hasLeft = !ornaments.placement || ornaments.placement === ElementPosition.LEFT;
            hasRight = ornaments.placement === ElementPosition.RIGHT;
        }
        let columns: string = '1fr';
        if (hasRight) columns = `${columns} fit-content(100%)`;
        if (hasLeft) columns = `fit-content(100%) ${columns}`;
        style.gridTemplateColumns = columns;
        return style;
    };

    renderLeftOrnament = () => {
        const {ornaments} = this.props;
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) return null;
        if (Array.isArray(ornaments)) {
            for (const ornament of ornaments) {
                if (!ornament.placement || ornament.placement === ElementPosition.LEFT)
                    return this.renderOrnament(ornament);
            }
        } else if (!ornaments.placement || ornaments.placement === ElementPosition.LEFT)
            return this.renderOrnament(ornaments);
        return null;
    };

    renderRightOrnament = () => {
        const {ornaments} = this.props;
        if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) return null;
        if (Array.isArray(ornaments)) {
            for (const ornament of ornaments) {
                if (ornament.placement === ElementPosition.RIGHT) return this.renderOrnament(ornament);
            }
        } else if (ornaments.placement === ElementPosition.RIGHT) return this.renderOrnament(ornaments);
        return null;
    };

    renderOrnament = (ornament: OrnamentShape) => {
        const {ornamentClassName} = this.props;
        if (typeof ornament.element === 'string')
            return <span className={classNames('bolmate-input-ornament', ornamentClassName)}>{ornament.element}</span>;
        else
            return React.cloneElement(ornament.element, {
                className: classNames(ornament.element.props.className, ornamentClassName)
            });
    };

    render = () => {
        const {
            className,
            disabled,
            labelClassName,
            label,
            onClick,
            secondary,
            tertiary,
            color = 'blue',
            size = 'normal',
            shape = 'square',
            align = 'left',
            id
        } = this.props;

        return (
            <div
                className={classNames(
                    'bolmate-button',
                    `color-${color}`,
                    `size-${size}`,
                    `shape-${shape}`,
                    `align-${align}`,
                    disabled && 'disabled',
                    secondary && 'secondary',
                    tertiary && 'tertiary',
                    className
                )}
                style={this.getContainerStyle()}
                onClick={disabled ? voidFunction : onClick}
                id={id}
            >
                {this.renderLeftOrnament()}
                <label className={classNames('bolmate-button-label', labelClassName)}>{label}</label>
                {this.renderRightOrnament()}
            </div>
        );
    };
}
